import * as React from "react";
import { Breadcrumb, Button, DropdownMenu, Separator } from "../ui";
import { useHistory } from "react-router-dom";
import { MoreHorizontalIcon } from "lucide-react";

export interface PageActionProps {
  text: string;
  icon?: string;
  disabled?: boolean;
  loading?: boolean;
  onClick: (event: any, data: any) => void;
}

export interface PageTitleProps {
  title: string;
  /** Pass `null` to keep overflow/secondary actions without promoting a menu item to primary. */
  primaryAction?: PageActionProps | null;
  secondaryAction?: PageActionProps;
  actions?: PageActionProps[];
  hideSeparator?: boolean;
  breadcrumbs?: { name: string; link: string | (() => void) }[];
}

const PageTitleFC: React.FC<PageTitleProps> = ({
  title,
  primaryAction,
  secondaryAction,
  actions,
  hideSeparator = false,
  breadcrumbs = [],
}) => {
  const history = useHistory();

  const getActionButtons = (): JSX.Element | null => {
    const explicitPrimary = primaryAction !== undefined;
    let finalPrimary = primaryAction ?? undefined;
    let finalSecondary = secondaryAction;
    const dropdownItems = actions ? [...actions] : [];

    // Legacy fallback when the caller only passed `actions`. An explicit `null`
    // primary means "no filled button" so overflow/secondary can stand alone.
    if (!explicitPrimary && dropdownItems.length > 0) {
      finalPrimary = dropdownItems.shift();
    }

    if (!explicitPrimary && !finalSecondary && dropdownItems.length === 1) {
      finalSecondary = dropdownItems.shift();
    }

    const overflowMenu =
      dropdownItems.length > 0 ? (
        <DropdownMenu>
          <DropdownMenu.Trigger
            render={
              <Button variant="outline" size="icon">
                <MoreHorizontalIcon />
              </Button>
            }
          />
          <DropdownMenu.Content>
            {dropdownItems.map((option, index) => (
              <DropdownMenu.Item
                key={index}
                onClick={() => option.onClick && option.onClick(null, null)}
                disabled={option.disabled}
              >
                {option.text}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu>
      ) : null;

    if (!finalPrimary && !finalSecondary && !overflowMenu) return null;

    return (
      <div className="flex items-center gap-2">
        {finalSecondary && (
          <Button
            variant="outline"
            disabled={finalSecondary.disabled}
            onClick={() => {
              finalSecondary.onClick && finalSecondary.onClick(null, null);
            }}
            loading={finalSecondary.loading}
          >
            {finalSecondary.text}
          </Button>
        )}
        {finalPrimary && (
          <Button
            disabled={finalPrimary.disabled}
            onClick={() => finalPrimary.onClick && finalPrimary.onClick(null, null)}
            loading={finalPrimary.loading}
          >
            {finalPrimary.text}
          </Button>
        )}
        {overflowMenu}
      </div>
    );
  };

  return (
    <div className="max-w-[1300px] pb-4 flex flex-col gap-7">
      <div className="flex flex-col gap-3 w-full min-w-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <Breadcrumb className="min-w-0">
          <Breadcrumb.List>
            {breadcrumbs.map((breadcrumb, index) => (
              <React.Fragment key={`breadcrumb-fragment-${index}`}>
                <Breadcrumb.Item>
                  <Breadcrumb.Link
                    className="text-xl font-bold sm:text-2xl"
                    onClick={() => {
                      if (typeof breadcrumb.link === "function") {
                        breadcrumb.link();
                      } else {
                        history.push(breadcrumb.link);
                      }
                    }}
                  >
                    {breadcrumb.name}
                  </Breadcrumb.Link>
                </Breadcrumb.Item>
                <Breadcrumb.Separator className="[&>svg]:size-5" />
              </React.Fragment>
            ))}
            <Breadcrumb.Item>
              <Breadcrumb.Page className="text-xl font-bold sm:text-2xl">{title}</Breadcrumb.Page>
            </Breadcrumb.Item>
          </Breadcrumb.List>
        </Breadcrumb>

        {getActionButtons()}
      </div>

      {hideSeparator || <Separator className="w-full h-px" />}
    </div>
  );
};

export const PageTitle = PageTitleFC;
